import { RouteObject } from "react-router-dom";
import ProtectedOutlet from "components/ProtectedOutlet";
import { ClaimResource } from "types/claimTypes";

import ProductsList from "pages/ProductsPage/ProductsList";
import ProductDetails from "pages/ProductsPage/ProductDetails";
import ProductCreatePage from "pages/ProductsPage/ProductCreatePage";
import ProductUpdatePage from "pages/ProductsPage/ProductUpdatePage";
import ProductVersions from "pages/ProductsPage/productVersions/ProductVersions";

import SkuList from "pages/SkusPage/SkuList";
import SkuForm from "pages/SkusPage/SkuForm";
import SkuFormDetails from "pages/SkusPage/SkuFormDetails";
import SkuRegistrations from "pages/SkuRegistrationsPage";
import SkuRegistrationsDownloadPage from "pages/SkuRegistrationsDownloadPage";

import SeriesList from "pages/SeriesPage/SeriesList";
import SeriesDetails from "pages/SeriesPage/SeriesDetails";
import SeriesUpdateForm from "pages/SeriesPage/SeriesUpdateForm";

import QuestionsList from "pages/QuestionsPage/QuestionsList";
import QuestionDetails from "pages/QuestionsPage/QuestionDetails";
import QuestionRatings from "pages/QuestionsPage/QuestionRatings";

import EvaluationsList from "pages/EvaluationsPage/EvaluationsList";
import EvaluationDetails from "pages/EvaluationsPage/EvaluationDetails";
import EvaluationCreateForm from "pages/EvaluationsPage/EvaluationCreateForm";
import EvaluationUpdateForm from "pages/EvaluationsPage/EvaluationUpdateForm";

import ResourceList from "pages/ResourcesPage/ResourceList";
import ResourceDetails from "pages/ResourcesPage/ResourceDetails";
import ResourceCreateForm from "pages/ResourcesPage/ResourceCreateForm";
import { ResourceSkus } from "pages/ResourcesPage/ResourceSkus";

import MediaLibraryItemsList from "pages/MediaLibraryPage/MediaLibraryItemsList";
import MediaLibraryItemDetails from "pages/MediaLibraryPage/MediaLibraryItemDetails";
import MediaLibraryItemCreateForm from "pages/MediaLibraryPage/MediaLibraryItemCreateForm";

import NotificationsPage from "pages/NotificationsPage";
import EmailTemplatesList from "pages/NotificationsPage/EmailTemplatesPage/EmailTemplatesList";
import EmailTemplateDetails from "pages/NotificationsPage/EmailTemplatesPage/EmailTemplateDetails";
import EmailTemplateCreateForm from "pages/NotificationsPage/EmailTemplatesPage/EmailTemplateCreateForm";
import EmailTemplateEditForm from "pages/NotificationsPage/EmailTemplatesPage/EmailTemplateEditForm";
import EmailThemeDetails from "pages/NotificationsPage/EmailThemePage/EmailThemeDetails";
import EmailThemeCreateForm from "pages/NotificationsPage/EmailThemePage/EmailThemeCreateForm";
import EmailThemeEditForm from "pages/NotificationsPage/EmailThemePage/EmailThemeEditForm";
import NotificationConfigurationsList from "pages/NotificationsPage/NotificationConfigurationsPage/NotificationConfigurationsList";
import NotificationConfigurationDetails from "pages/NotificationsPage/NotificationConfigurationsPage/NotificationConfigurationDetails";
import NotificationConfigurationCreateForm from "pages/NotificationsPage/NotificationConfigurationsPage/NotificationConfigurationCreateForm";
import NotificationConfigurationEditForm from "pages/NotificationsPage/NotificationConfigurationsPage/NotificationConfigurationEditForm";

import ResourceLibrariesList from "pages/ResourceLibrariesPage/ResourceLibrariesList";
import ResourceLibraryDetails from "pages/ResourceLibrariesPage/ResourceLibraryDetails";
import ResourceLibraryItemDetails from "pages/ResourceLibrariesPage/ResourceLibraryItemDetails";

import AddExportRusticiCourses from "pages/AddExportRusticiCourses";
import DeactivateRusticiCourses from "pages/DeactivateRusticiCourses";
import ExportedScormFiles from "pages/ExportedScormFiles";
import GenerateScormMetadata from "pages/GenerateScormMetadata";
import ScormPage from "pages/ScormPage";

import AgreementsList from "pages/AgreementsPage/AgreementsList";
import AgreementDetails from "pages/AgreementsPage/AgreementDetails";
import AgreementCreate from "pages/AgreementsPage/AgreementCreate";
import AgreementEdit from "pages/AgreementsPage/AgreementEdit";

import UserAgreementList from "pages/UserAgreementsPage/UserAgreementList";
import { UserAgreementCreateForm } from "pages/UserAgreementsPage/UserAgreementCreateForm";
import UserAgreementDetails from "pages/UserAgreementsPage/UserAgreementDetails";

import { AttributesPage } from "pages/AttributesPage";
import AttributeListWrapper from "pages/AttributesPage/AttributeListWrapper";
import { AttributeDetails } from "pages/AttributesPage/AttributeDetails";
import { AttributeCreateForm } from "pages/AttributesPage/AttributeCreateForm";
import { AttributeUpdateForm } from "pages/AttributesPage/AttributeUpdateForm";

import ContentPage from "pages/ContentPage";

export const contentRoutes: RouteObject[] = [
  {
    element: <ProtectedOutlet claims={[ClaimResource.Products]} />,
    children: [
      { path: "products", element: <ProductsList /> },
      { path: "products/new", element: <ProductCreatePage /> },
      { path: "products/edit/:id", element: <ProductUpdatePage /> },
      { path: "products/-/:id", element: <ProductDetails /> },
      { path: "products/versions", element: <ProductVersions /> },
    ],
  },
  {
    element: <ProtectedOutlet claims={[ClaimResource.Skus]} />,
    children: [
      { path: "skus", element: <SkuList /> },
      { path: "skus/new/:productId", element: <SkuForm /> },
      { path: "skus/edit/:id", element: <SkuForm /> },
      { path: "skus/-/:id", element: <SkuFormDetails /> },
      { path: "skus/-/:id/registrations", element: <SkuRegistrations /> },
      {
        path: "skus/-/:id/registrations/download/:fileId",
        element: <SkuRegistrationsDownloadPage />,
      },
    ],
  },
  {
    element: <ProtectedOutlet claims={[ClaimResource.Series]} />,
    children: [
      { path: "series", element: <SeriesList /> },
      { path: "series/-/:id", element: <SeriesDetails /> },
      { path: "series/edit/:id", element: <SeriesUpdateForm /> },
    ],
  },
  {
    element: <ProtectedOutlet claims={[ClaimResource.Questions]} />,
    children: [
      { path: "questions", element: <QuestionsList /> },
      { path: "questions/-/:id", element: <QuestionDetails /> },
      { path: "questions/ratings", element: <QuestionRatings /> },
    ],
  },
  {
    element: <ProtectedOutlet claims={[ClaimResource.Evaluations]} />,
    children: [
      { path: "evaluations", element: <EvaluationsList /> },
      { path: "evaluations/new", element: <EvaluationCreateForm /> },
      { path: "evaluations/-/:id", element: <EvaluationDetails /> },
      { path: "evaluations/edit/:id", element: <EvaluationUpdateForm /> },
    ],
  },
  {
    element: <ProtectedOutlet claims={[ClaimResource.Resources]} />,
    children: [
      { path: "resources", element: <ResourceList /> },
      { path: "resources/new", element: <ResourceCreateForm /> },
      { path: "resources/:id", element: <ResourceDetails /> },
      { path: "resources/-/:id/skus", element: <ResourceSkus /> },
    ],
  },
  {
    element: <ProtectedOutlet claims={[ClaimResource.MediaLibrary]} />,
    children: [
      { path: "media-library", element: <MediaLibraryItemsList /> },
      {
        path: "media-library/new-media",
        element: <MediaLibraryItemCreateForm />,
      },
      { path: "media-library/items/:id", element: <MediaLibraryItemDetails /> },
    ],
  },
  {
    element: <ProtectedOutlet claims={[ClaimResource.Notifications]} />,
    children: [
      { path: "notifications", element: <NotificationsPage /> },
      {
        path: "notifications/configurations",
        element: <NotificationConfigurationsList />,
      },
      {
        path: "notifications/configurations/new",
        element: <NotificationConfigurationCreateForm />,
      },
      {
        path: "notifications/configurations/:id",
        element: <NotificationConfigurationDetails />,
      },
      {
        path: "notifications/configurations/:id/edit",
        element: <NotificationConfigurationEditForm />,
      },
      { path: "notifications/email-templates", element: <EmailTemplatesList /> },
      {
        path: "notifications/email-templates/new",
        element: <EmailTemplateCreateForm />,
      },
      {
        path: "notifications/email-templates/:id",
        element: <EmailTemplateDetails />,
      },
      {
        path: "notifications/email-templates/:id/edit",
        element: <EmailTemplateEditForm />,
      },
      { path: "notifications/email-themes", element: <EmailThemeDetails /> },
      {
        path: "notifications/email-themes/new",
        element: <EmailThemeCreateForm />,
      },
      {
        path: "notifications/email-themes/edit",
        element: <EmailThemeEditForm />,
      },
    ],
  },
  {
    element: <ProtectedOutlet claims={[ClaimResource.ResourceLibraries]} />,
    children: [
      { path: "resource-libraries", element: <ResourceLibrariesList /> },
      { path: "resource-libraries/-/:id", element: <ResourceLibraryDetails /> },
      {
        path: "resource-libraries/:rid/items/-/:id",
        element: <ResourceLibraryItemDetails />,
      },
    ],
  },
  {
    element: <ProtectedOutlet claims={[ClaimResource.Rustici]} />,
    children: [
      { path: "scorm", element: <ScormPage /> },
      {
        path: "scorm/deactivate-rustici",
        element: <DeactivateRusticiCourses />,
      },
      {
        path: "scorm/add-export-courses",
        element: <AddExportRusticiCourses />,
      },
      {
        path: "scorm/exported-files",
        element: <ExportedScormFiles />,
      },
      {
        path: "scorm/generate-metadata",
        element: <GenerateScormMetadata />,
      },
    ],
  },
  {
    element: <ProtectedOutlet claims={[ClaimResource.Agreements]} />,
    children: [
      { path: "agreements", element: <AgreementsList /> },
      { path: "agreements/new", element: <AgreementCreate /> },
      { path: "agreements/-/:id", element: <AgreementDetails /> },
      { path: "agreements/edit/:id", element: <AgreementEdit /> },
      { path: "agreements/user-agreements", element: <UserAgreementList /> },
      {
        path: "agreements/user-agreements/new",
        element: <UserAgreementCreateForm />,
      },
      {
        path: "agreements/user-agreements/:id",
        element: <UserAgreementDetails />,
      },
    ],
  },
  {
    element: <ProtectedOutlet claims={[ClaimResource.Attributes]} />,
    children: [
      { path: "attributes", element: <AttributesPage /> },
      { path: "attributes/new", element: <AttributeCreateForm /> },
      { path: "attributes/edit/:id", element: <AttributeUpdateForm /> },
      { path: "attributes/:id", element: <AttributeDetails /> },
      { path: "attributes/by-type/:code", element: <AttributeListWrapper /> },
    ],
  },
  { index: true, element: <ContentPage /> },
];
